import React, { useState } from 'react';
import { InfoIcon } from 'lucide-react';
import { WindowFrame } from '../ui/WindowFrame';
import { StatusPill } from '../ui/StatusPill';
import type { WorkspacePreviewData } from '../../types/content';
export function WorkspacePreview({
  data,
  workspaceName
}: {
  data: WorkspacePreviewData;
  workspaceName: string;
}) {
  const [filter, setFilter] = useState(0);
  const {
    detail
  } = data;
  return <WindowFrame title={`${workspaceName} · ${data.surface}`} context={data.title} data-id="element-415" data-mp="x0bw558l4qf">
      <div className="grid md:grid-cols-[1fr_1.05fr]" data-id="element-416" data-mp="5wcw0i4q3xpb">
        <div className="border-b border-ink/[0.06] p-4 md:border-b-0 md:border-r" data-id="element-417" data-mp="5v1vcr6kr62f">
          <div className="flex items-center justify-between" data-id="element-418" data-mp="aomskh0cfw0k">
            <p className="text-sm font-semibold" data-id="element-419" data-mp="fn7jtqxmb5cv">{data.title}</p>
            <span className="text-[11px] text-ink-muted" data-id="element-420" data-mp="km0yvnsjz4f5">{data.rows.length} items</span>
          </div>
          <div className="mt-3 flex gap-1" role="group" aria-label="Sample filters" data-id="element-421" data-mp="onm7uy1yuyk2">
            {data.filters.map((f, i) => <button key={f} type="button" onClick={() => setFilter(i)} aria-pressed={filter === i} className={`whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors duration-150 ${filter === i ? 'bg-ink text-white' : 'text-ink-muted hover:bg-ink/[0.05]'}`} data-id="element-422" data-mp="1pjqinc5hk1fm" data-mpi={i}>
                {f}
              </button>)}
          </div>
          <ul className="mt-3 space-y-1" data-id="element-423" data-mp="23alkw5lhbka8">
            {data.rows.map((row, i) => <li key={row.title} className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2.5 ${i === 0 ? 'bg-white shadow-glow' : ''}`} data-id="element-424" data-mp="1yjn3t3so2i5x" data-mpi={i}>
                <div className="min-w-0" data-id="element-425" data-mp="2r17rdsfu4wi4">
                  <p className="truncate text-[13px] font-medium" data-id="element-426" data-mp="2m9psd9xdh5zs">{row.title}</p>
                  <p className="truncate text-[11px] text-ink-muted" data-id="element-427" data-mp="2m7muk3pza8ki">{row.meta}</p>
                </div>
                <StatusPill tone={row.tone} data-id="element-428" data-mp="kea5gb8i2ef9">{row.status}</StatusPill>
              </li>)}
          </ul>
        </div>

        <div className="flex flex-col p-5" data-id="element-429" data-mp="amnr460v7z8a">
          <p className="text-[11px] text-ink-muted" data-id="element-430" data-mp="alup1icupxwh">{detail.subtitle}</p>
          <p className="mt-1 text-base font-semibold leading-snug tracking-tight" data-id="element-431" data-mp="fkpa6qyajyjr">{detail.title}</p>
          <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3" data-id="element-432" data-mp="1kmsb4pzh54i">
            {detail.fields.map(f => <div key={f.label} data-id="element-433" data-mp="5och9vakloq2">
                <dt className="text-[11px] text-ink-muted" data-id="element-434" data-mp="5l6pu9egyar6">{f.label}</dt>
                <dd className="text-[13px] font-medium" data-id="element-435" data-mp="3nu7gsniedsf6">{f.value}</dd>
              </div>)}
          </dl>
          <p className="mt-5 text-[11px] font-medium text-ink-muted" data-id="element-436" data-mp="3ss927f3aq3tt">Activity</p>
          <ol className="mt-2 space-y-1.5" data-id="element-437" data-mp="1m2udg2w2vsl8">
            {detail.timeline.map(t => <li key={t.label} className="flex items-center gap-2 text-[12px]" data-id="element-438" data-mp="1qzkscmw5ci52">
                <span className="h-1.5 w-1.5 rounded-full bg-ink/25" aria-hidden="true" data-id="element-439" data-mp="3bhvf0pguoqbi" />
                <span className="flex-1 text-ink-soft" data-id="element-440" data-mp="36k3n3vg7h47b">{t.label}</span>
                <span className="tabular-nums text-ink-faint" data-id="element-441" data-mp="36hqwyv0annj6">{t.time}</span>
              </li>)}
          </ol>
          {detail.note ? <p className="mt-4 flex gap-2 rounded-xl bg-accent-soft px-3 py-2 text-[12px] text-accent-ink" data-id="element-442" data-mp="3ggq4295evb42">
              <InfoIcon className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" data-id="element-443" data-mp="3u7v256t4rvca" />
              {detail.note}
            </p> : null}
          <div className="mt-auto flex flex-wrap gap-2 pt-5" data-id="element-444" data-mp="81y0k4bnow7d">
            <span className="inline-flex h-8 items-center rounded-full bg-accent px-3.5 text-[12px] font-medium text-white" data-id="element-445" data-mp="81g00vhynvtn">
              {detail.primaryAction}
            </span>
            <span className="inline-flex h-8 items-center rounded-full px-3 text-[12px] font-medium text-ink-soft ring-1 ring-ink/10" data-id="element-446" data-mp="29u43n5yiol9y">
              {detail.secondaryAction}
            </span>
          </div>
        </div>
      </div>
    </WindowFrame>;
}